import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCategoryPricing,
  usePricingMutations,
} from "../../../hooks/usePricing";
import {
  CategoryPricing,
  CreateProductPricingRequest,
  SpecificProductPricing,
  UpdateProductPricingRequest,
} from "../../../types/admin";

interface ProductPricingModalProps {
  open: boolean;
  mode: "create" | "edit";
  product?: SpecificProductPricing;
  onClose: () => void;
  onSuccess: () => void;
}

const ProductPricingModal: React.FC<ProductPricingModalProps> = ({
  open,
  mode,
  product,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateProductPricingRequest>({
    productKeyword: "",
    categoryId: "",
    fixedPrice: 0,
    description: "",
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createProductPricing, updateProductPricing } = usePricingMutations();
  const { data: categoriesData } = useCategoryPricing();
  const categories: CategoryPricing[] = (categoriesData?.data?.items || categoriesData?.data || []) as CategoryPricing[];

  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    if (open) {
      if (isEditMode && product) {
        setFormData({
          productKeyword: product.productKeyword,
          categoryId: product.categoryId,
          fixedPrice: product.fixedPrice,
          description: product.description || "",
          isActive: product.isActive,
        });
      } else {
        setFormData({
          productKeyword: "",
          categoryId: "",
          fixedPrice: 0,
          description: "",
          isActive: true,
        });
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, mode, product, isEditMode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.productKeyword.trim()) {
      newErrors.productKeyword = t("admin.productKeywordRequired");
    }

    if (!formData.categoryId.trim()) {
      newErrors.categoryId = t("admin.categoryRequired");
    }

    if (!formData.fixedPrice || formData.fixedPrice <= 0) {
      newErrors.fixedPrice = t("admin.fixedPriceMustBeGreaterThanZero");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (
    field: keyof CreateProductPricingRequest,
    value: any,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (isCreateMode) {
        await createProductPricing.mutateAsync(formData);
      } else if (isEditMode && product) {
        const updateData: UpdateProductPricingRequest = {
          ...formData,
        };
        await updateProductPricing.mutateAsync({
          id: product.id,
          data: updateData,
        });
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving product pricing:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading =
    isSubmitting ||
    createProductPricing.isPending ||
    updateProductPricing.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isCreateMode
          ? t("admin.createProductPricingRule")
          : t("admin.editProductPricingRule")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.productKeyword")}
                value={formData.productKeyword}
                onChange={(e) =>
                  handleFieldChange("productKeyword", e.target.value)
                }
                error={!!errors.productKeyword}
                helperText={
                  errors.productKeyword || t("admin.productKeywordHelperText")
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.categoryId}>
                <InputLabel>{t("admin.category")}</InputLabel>
                <Select
                  value={formData.categoryId}
                  onChange={(e) =>
                    handleFieldChange("categoryId", e.target.value)
                  }
                  label={t("admin.category")}
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.categoryName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.categoryId && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.fixedPrice")}
                type="number"
                value={formData.fixedPrice}
                onChange={(e) =>
                  handleFieldChange(
                    "fixedPrice",
                    parseFloat(e.target.value) || 0,
                  )
                }
                error={!!errors.fixedPrice}
                helperText={
                  errors.fixedPrice || t("admin.fixedPriceProductHelperText")
                }
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("admin.description")}
                value={formData.description}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
                multiline
                rows={3}
                helperText={t("admin.optionalPricingRuleDescription")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleFieldChange("isActive", e.target.checked)
                    }
                  />
                }
                label={t("common.active")}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t("common.saving") : isCreateMode ? t("common.create") : t("common.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductPricingModal;
